import React from 'react';
import PropTypes from 'prop-types';
import { Text, Badge, Spacing, Icon } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import { safeParsePrice } from 'utils/currency';
import CustomTooltip from './CustomTooltip';

/**
 * PricingRow component.
 * Renders a row with label and price, supporting discount indicators and tooltips.
 * @param {object} props - isMain, label, discountLabel, hasDiscount, price, isNumber, icon, iconLabel, primary, tooltipContent, isSpaceBetween, priceAlignment, description.
 * @param {boolean} props.isMain - Whether this is the main pricing row with bold styling.
 * @param {string} props.label - The label text for the pricing item.
 * @param {string} props.discountLabel - Optional discount badge label.
 * @param {boolean} props.hasDiscount - Whether to show discount sign.
 * @param {number|string} props.price - The price value to display.
 * @param {boolean} props.isNumber - Whether to show price as plain number instead of currency.
 * @param {string} props.icon - Optional icon to display next to price.
 * @param {string} props.iconLabel - Optional icon to display next to label.
 * @param {boolean} props.primary - Whether to use primary color styling.
 * @param {string|string[]} props.tooltipContent - Optional tooltip content.
 * @param {number} props.tooltipWidth - Optional tooltip width in pixels.
 * @param {boolean} props.useFixedTooltip - Use position fixed tooltip to escape overflow containers.
 * @param {boolean} props.isSpaceBetween - Whether to use space-between layout.
 * @param {string} props.priceAlignment - Alignment for price section.
 * @param {string} props.description - Optional description text.
 * @param {string} props.titleSize - Optional size for the title.
 * @returns {JSX.Element} The rendered pricing row component.
 */
const PricingRow = ({
  isMain,
  label,
  discountLabel,
  hasDiscount,
  price,
  isNumber,
  icon,
  iconLabel,
  primary,
  tooltipContent,
  tooltipWidth,
  useFixedTooltip,
  isSpaceBetween,
  priceAlignment,
  priceJustify,
  description,
  titleSize,
}) => {
  const fontColor = primary ? 'primary' : 'grayLight';
  const parsedPrice = safeParsePrice(price);

  return (
    <div
      className={
        isSpaceBetween
          ? 'flex justify-between items-center'
          : 'grid items-center grid-flow-col grid-cols-2'
      }
    >
      <Spacing vertical size="XS" alignItems="flex-start">
        <Spacing size="XS" alignItems="center">
          <Text
            size={titleSize || 'S'}
            color={isMain ? 'grayStrong' : fontColor}
            weight={isMain ? 'semibold' : 'regular'}
            whiteSpace={tooltipContent ? 'nowrap' : null}
          >
            {`${label} `}
            {description && (
              <Text size="S" weight="bold" elementType="span">
                {` ${description}`}
              </Text>
            )}
          </Text>

          {iconLabel && <Icon size="S" type={iconLabel} />}

          <CustomTooltip content={tooltipContent} width={tooltipWidth} useFixed={useFixedTooltip} />
        </Spacing>

        {Boolean(discountLabel) && (
          <Badge smallPadding type="success">
            <Text color="white" elementType="span" size="S" weight="semibold">
              {discountLabel}
            </Text>
          </Badge>
        )}
      </Spacing>

      <Spacing alignItems={priceAlignment} justifyContent={priceJustify} size="XS">
        {icon ? <Icon size="S" type={icon} /> : null}
        <LocalizedCurrency
          size={titleSize || 'S'}
          color={isMain ? 'grayStrong' : fontColor}
          weight={isMain ? 'semibold' : 'regular'}
          price={hasDiscount ? parsedPrice * -1 : parsedPrice}
          currencyStyle={isNumber ? 'decimal' : 'currency'}
        />
      </Spacing>
    </div>
  );
};

PricingRow.propTypes = {
  label: PropTypes.string,
  discountLabel: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isMain: PropTypes.bool,
  hasDiscount: PropTypes.bool,
  isNumber: PropTypes.bool,
  icon: PropTypes.string,
  iconLabel: PropTypes.string,
  primary: PropTypes.bool,
  tooltipContent: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  tooltipWidth: PropTypes.number,
  useFixedTooltip: PropTypes.bool,
  isSpaceBetween: PropTypes.bool,
  priceAlignment: PropTypes.string,
  priceJustify: PropTypes.string,
  description: PropTypes.string,
  titleSize: PropTypes.string,
};

PricingRow.defaultProps = {
  label: '',
  discountLabel: '',
  isMain: false,
  hasDiscount: false,
  isNumber: false,
  icon: '',
  iconLabel: '',
  primary: false,
  tooltipContent: '',
  tooltipWidth: 220,
  useFixedTooltip: false,
  isSpaceBetween: false,
  priceAlignment: 'center',
  priceJustify: 'flex-end',
  description: '',
};

export default PricingRow;
